import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CustomCategoriesPageDocument,
  CustomCategoryDetailDocument,
  FinanceCategoryLedgerAccountInput,
  FinanceCustomCategoryItemFragment,
  FinanceCustomCategoryLegalEntityFragment,
  FinanceCustomSubcategoryItemFragment,
  UpdateFinanceCustomSubcategoryDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LedgerAccountAssigmentForm from 'modules/finance/components/CustomCategories/LedgerAccountAssignmentForm'
import { useLedgerAccountAssignment } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'

interface EditCustomSubcategoryForm {
  label: string
  parentCategoryId: string
  assignedLedgerAccounts: FinanceCategoryLedgerAccountInput[]
}

interface Props {
  customCategory: FinanceCustomSubcategoryItemFragment
  parentCategories: FinanceCustomCategoryItemFragment[]
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  canEditLedgerAccountAssignments: boolean
  onClose: () => void
}

const EditCustomSubcategoryModal = ({
  customCategory,
  parentCategories,
  legalEntities,
  canEditLedgerAccountAssignments,
  onClose,
}: Props) => {
  const { data: assignedLedgerAccountsData } = useQuery(CustomCategoryDetailDocument, {
    variables: { resourceIds: [customCategory.id] },
  })

  const assignedLedgerAccountResources =
    assignedLedgerAccountsData?.finance.ledgerAccountResourcesConnection?.nodes ?? []

  const [updateFinanceCustomSubcategory] = useMutation(UpdateFinanceCustomSubcategoryDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceCustomCategory,
  })

  const parentCategoryOptions = parentCategories.map((category) => ({
    label: category.label,
    value: category.id,
  }))

  const currentResourceAssignments: FinanceCategoryLedgerAccountInput[] =
    assignedLedgerAccountResources.map((assignedResource) => ({
      ledgerAccountId: assignedResource.account.id,
      legalEntityId: Number(assignedResource.account.legalEntityId),
      ledgerAccountResourceId: assignedResource.id,
    }))

  const form = useForm<EditCustomSubcategoryForm>({
    defaultValues: {
      label: customCategory.label,
      parentCategoryId: customCategory?.parentCategoryId?.toString() || '',
      assignedLedgerAccounts: currentResourceAssignments,
    },
    validate: () => {
      const errors: FormErrors<EditCustomSubcategoryForm> = {}
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateFinanceCustomSubcategory({
        variables: {
          id: customCategory.id,
          label: data.label,
          deleted: false,
          enabled: customCategory.enabled,
          parentCategoryId: Number(data.parentCategoryId),
          position: 0,
          visible: true,
          ledgerAccountAssignments: data.assignedLedgerAccounts,
        },
        refetchQueries: [CustomCategoriesPageDocument, CustomCategoryDetailDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.editSubcategory)
        return res
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
        app.setFlash({
          type: 'wrong',
          message: errors.messages || i18n.t('errors.generic_error'),
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        handleClose()
      }
      return handled
    },
  })

  const handleSelectLedgerAccount = useLedgerAccountAssignment(form.setValues)

  const handleSave = async () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.settings.accounting.custom_categories.modals.edit_subcategory.title')}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={form.isSubmitting || !form.values.label || !form.values.parentCategoryId}
          >
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.update')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s16">
        <TextField
          label={i18n.t(
            'finance.settings.accounting.custom_categories.modals.edit_subcategory.fields.name'
          )}
          onChange={(value) => form.setValues({ ...form.values, label: value })}
          value={form.values.label}
          error={form.errors.label}
        />
        <SelectField
          label={i18n.t(
            'finance.settings.accounting.custom_categories.modals.edit_subcategory.fields.parent'
          )}
          options={parentCategoryOptions}
          onChange={(value) => form.setValues({ ...form.values, parentCategoryId: value })}
          value={form.values.parentCategoryId}
          error={form.errors.parentCategoryId}
          mandatory
        />
        {canEditLedgerAccountAssignments && (
          <LedgerAccountAssigmentForm
            legalEntities={legalEntities}
            currentAssignments={currentResourceAssignments.map((assignment) => ({
              ...assignment,
              operationType: undefined,
            }))}
            onChange={handleSelectLedgerAccount}
          />
        )}
      </Box>
    </Modal>
  )
}

export default EditCustomSubcategoryModal
